/**
 * <s-sticky-buy> — buy bar on product pages (snippets/product-sticky-bar.liquid).
 * - Desktop: visible once the main form's Add to cart row has scrolled above the viewport; hidden when it is on
 *   screen or below it (top of page). Uses IntersectionObserver, no scroll handler.
 * - Phones (merchant 2026-11-01): the page shows its own Add to cart + express checkout; the bar is visible whenever
 *   that row is off screen (above or below), so a buy button is always on screen without showing two at once.
 * - Add to cart submits the real product form with the real Add to cart button as submitter, so
 *   <s-product-form> handles variant, quantity and the cart drawer exactly as usual.
 * - Mirrors the main price block, the sold-out state and the chosen style's thumbnail after variant changes
 *   (the 'product-form' and 'product-title' partials are swapped in place, so it re-queries and watches with a
 *   MutationObserver). The thumbnail comes from the variant sheet row of the chosen variant.
 */
const phoneQuery = window.matchMedia('(max-width: 47.999rem)');

class SStickyBuy extends HTMLElement {
  #io = null;
  #mo = null;
  #observed = null;
  #rowInView = false;
  #button = null;
  #price = null;
  #thumb = null;
  #scrolledPast = false;
  #ac = null;

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    this.#button = this.querySelector('[data-sticky-add]');
    this.#price = [...this.querySelectorAll('[data-sticky-price]')];
    this.#thumb = this.querySelector('[data-sticky-thumb]');
    this.#button?.addEventListener('click', this.#onClick, { signal });
    phoneQuery.addEventListener('change', () => this.#applyVisibility(), { signal });
    this.#io = new IntersectionObserver(this.#onIntersect);
    this.#mo = new MutationObserver(() => this.#sync());
    const root = document.querySelector('[data-quick-add-source]') ?? document.body;
    this.#mo.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['value'] });
    this.#sync();
    this.#applyVisibility();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#io?.disconnect();
    this.#mo?.disconnect();
  }

  #mainAdd() {
    return document.querySelector('[data-quick-add-source] [data-product-form] [name="add"]');
  }

  #mainSoldOut() {
    return document.querySelector('[data-quick-add-source] [data-product-form] button[inert], [data-quick-add-source] [data-product-form] button[disabled]');
  }

  #sync() {
    // Observe the row that holds the main buy button (re-created on variant change).
    const anchor = (this.#mainAdd() ?? this.#mainSoldOut())?.parentElement ?? null;
    if (anchor && anchor !== this.#observed) {
      if (this.#observed) this.#io.unobserve(this.#observed);
      this.#observed = anchor;
      this.#io.observe(anchor);
    }

    const available = Boolean(this.#mainAdd());
    if (this.#button) {
      this.#button.disabled = !available;
      // Mirrors the main button's "Add to cart · $…" (variant × quantity + add-ons, merchant 2026-10-30).
      const mainLabel = this.#mainAdd()?.textContent.trim();
      const label = available ? (mainLabel || this.#button.dataset.labelAdd) : this.#button.dataset.labelSoldOut;
      if (label && this.#button.textContent.trim() !== label) this.#button.textContent = label;
    }

    // Phone: the selling-point tags on the photo follow the chosen variant (source re-rendered in the price partial).
    const tagsSource = document.querySelector('[data-quick-add-source] [data-overlay-tags-source]');
    const tagsTarget = document.querySelector('[data-overlay-tags]');
    if (tagsSource && tagsTarget && tagsTarget.innerHTML !== tagsSource.innerHTML) {
      tagsTarget.innerHTML = tagsSource.innerHTML;
    }

    const mainPrice = document.querySelector('[data-quick-add-source] [data-product-price] > :first-child');
    for (const priceEl of mainPrice ? this.#price : []) {
      if (priceEl.innerHTML !== mainPrice.innerHTML) priceEl.innerHTML = mainPrice.innerHTML;
    }

    const id = document.querySelector('[data-quick-add-source] [data-product-form] input[name="id"]')?.value;
    const rowImage = id ? document.querySelector(`[data-sticky-variant-thumb="${CSS.escape(id)}"] img`) : null;
    const rowSrc = rowImage?.currentSrc || rowImage?.src;
    if (this.#thumb && rowSrc && this.#thumb.src !== rowSrc) {
      this.#thumb.removeAttribute('srcset');
      this.#thumb.src = rowSrc;
    }
  }

  #applyVisibility() {
    const visible = phoneQuery.matches ? !this.#rowInView : this.#scrolledPast;
    this.toggleAttribute('data-visible', visible);
    this.inert = !visible;
    document.documentElement.toggleAttribute('data-sticky-buy', visible);
  }

  #onIntersect = (entries) => {
    for (const entry of entries) {
      if (entry.target !== this.#observed) continue;
      // A hidden row (phones) never intersects; only count it as "scrolled past" when it really sits above the viewport.
      this.#scrolledPast = !entry.isIntersecting && entry.boundingClientRect.bottom < 0 && entry.boundingClientRect.height > 0;
      this.#rowInView = entry.isIntersecting && entry.boundingClientRect.height > 0;
      this.#applyVisibility();
    }
  };

  #onClick = () => {
    const add = this.#mainAdd();
    const form = add?.form;
    if (!add || !form) return;
    if (typeof form.requestSubmit === 'function') form.requestSubmit(add);
    else add.click();
  };
}

if (!customElements.get('s-sticky-buy')) customElements.define('s-sticky-buy', SStickyBuy);

// Phone "Service" row: opens and scrolls to the <details> it names (data-open-details="id").
document.addEventListener('click', (event) => {
  const trigger = event.target.closest?.('[data-open-details]');
  if (!trigger) return;
  const details = document.getElementById(trigger.dataset.openDetails);
  if (!details) return;
  details.open = true;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  details.scrollIntoView({ behavior: reduced ? 'instant' : 'smooth', block: 'start' });
});
